import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 13);
      return;
    }
    const bounds = points.map((p) => [p.lat, p.lng]);
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [points, map]);
  return null;
}

export default function ProspectionMap({ entries = [], height = 500 }) {
  // Group by rounded coord to compute counts per zone
  const grouped = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      if (e.lat == null || e.lng == null) continue;
      // Round to ~100m precision for zone counting
      const key = `${e.lat.toFixed(3)},${e.lng.toFixed(3)}`;
      const g = map.get(key) || { lat: e.lat, lng: e.lng, items: [] };
      g.items.push(e);
      map.set(key, g);
    }
    return Array.from(map.values());
  }, [entries]);

  const points = grouped.map((g) => ({ lat: g.lat, lng: g.lng }));
  const defaultCenter = [46.6, 2.5]; // Center of France

  return (
    <div className="rounded-lg overflow-hidden border border-border" style={{ height }} data-testid="prospection-map">
      <MapContainer
        center={defaultCenter}
        zoom={6}
        scrollWheelZoom
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={points} />
        {grouped.map((g) => {
          const count = g.items.length;
          const radius = Math.min(10 + count * 3, 26);
          const colors = new Set(g.items.map((i) => i.user_color));
          const color = colors.size === 1 ? g.items[0].user_color : "#0F172A";
          const key = `${g.lat.toFixed(4)},${g.lng.toFixed(4)}`;
          return (
            <CircleMarker
              key={key}
              center={[g.lat, g.lng]}
              radius={radius}
              pathOptions={{
                color: "#ffffff",
                weight: 2,
                fillColor: color,
                fillOpacity: 0.75,
              }}
            >
              <Popup>
                <div style={{ minWidth: 200 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    {count} passage{count > 1 ? "s" : ""}
                  </div>
                  <div style={{ fontSize: 12, color: "#475569", marginBottom: 6 }}>
                    {g.items[0].display_name || g.items[0].address}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {g.items.slice(0, 6).map((i) => (
                      <div key={i.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                        <span
                          style={{
                            display: "inline-block",
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: i.user_color,
                          }}
                        />
                        <span style={{ fontWeight: 600 }}>{i.user_name}</span>
                        <span style={{ color: "#94A3B8", marginLeft: "auto" }}>
                          {new Date(i.created_at).toLocaleDateString("fr-FR")}
                        </span>
                      </div>
                    ))}
                    {g.items.length > 6 ? (
                      <div style={{ fontSize: 11, color: "#94A3B8" }}>+{g.items.length - 6} autres</div>
                    ) : null}
                  </div>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
}
